import type { Microsoft365Feature } from '@dbrain/shared';
import { Alert, Button, ConfirmDialog, Icons } from '@dbrain/ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCompanyZone } from '../../api/hooks.js';
import { useMicrosoft365Disconnect, useMicrosoft365Status } from '../../api/microsoft365.js';
import { humanTime } from '@dbrain/app-core/format';
import { ErrorAlert } from '../ErrorAlert.js';
import { SettingGroup } from '../settings/rows.js';
import { Microsoft365ConnectDialog } from './ConnectDialog.js';
import { MicrosoftIcon } from './MicrosoftIcon.js';
import { ConsentRequestButton } from './parts.js';
import { Microsoft365Result } from './Result.js';
import { cardView, linkedRows, linkedVisible, offReason } from './state.js';

/**
 * Ayarlar › Hesap › Bağlı hesaplar (MsBagliHesaplar): the Microsoft 365 account with when it was connected and last
 * used, what Oppy may do (Outlook mail, Outlook takvim; part 3's sending and meetings "Her seferinde onayınızla",
 * OneDrive and SharePoint while the firm has them on, or "İzin gerekiyor" with "Yeniden bağlan" while the account has
 * not granted the permission yet), and "Bağlantıyı kaldır", which deletes Oppy's key. Not connected yet: "Hesabımı
 * bağla". Shown only where there is an account to see or one to connect (never in a personal space, nor while the
 * firm has not set it up).
 */
export function LinkedAccountsGroup() {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const status = useMicrosoft365Status().data;
  const disconnect = useMicrosoft365Disconnect();
  const [connecting, setConnecting] = useState(false);
  const [upgrade, setUpgrade] = useState<Microsoft365Feature[] | null>(null);
  const [removing, setRemoving] = useState(false);
  const [connected, setConnected] = useState(false);
  // back from Microsoft to an account that is not shown here (turned off meanwhile): the result still says why
  if (!status || !linkedVisible(status)) return <Microsoft365Result />;
  const view = cardView(status);
  const account = status.account;
  const when = (iso: string) => humanTime(iso, i18n.language, t, true, zone);
  const meta = account
    ? [t('microsoft365.user.linked.account', { email: account.email, connected: when(account.connectedAt) }), account.lastUsedAt ? t('microsoft365.user.linked.lastUsed', { used: when(account.lastUsedAt) }) : null]
        .filter(Boolean)
        .join(' · ')
    : t('microsoft365.user.linked.notConnected');
  return (
    <SettingGroup title={t('microsoft365.user.linked.title')} data-testid="settings-linked-accounts">
      <div className="m365-linked__alerts">
        <Microsoft365Result />
        {connected ? <Alert tone="success" title={t('microsoft365.user.result.connected')} data-testid="microsoft365-result" /> : null}
      </div>
      <section className={view.muted ? 'm365-linked m365-linked--muted' : 'm365-linked'} aria-label={t('microsoft365.user.name')} data-testid="linked-microsoft365" data-state={status.state}>
        <div className="m365-linked__head">
          <span className="m365-tile m365-tile--sm" aria-hidden="true">
            <Icons.connections size={18} />
          </span>
          <div className="m365-linked__title">
            <span className="m365-card__name">
              <strong>{t('microsoft365.user.name')}</strong>
              <span className={`connector-state connector-state--${view.tone}`} data-testid="linked-microsoft365-state">
                {t(`microsoft365.user.card.state.${view.pill}`)}
              </span>
            </span>
            <span className="m365-linked__meta" data-testid="linked-microsoft365-meta">
              {meta}
            </span>
          </div>
          {view.action === 'connect' || view.action === 'reconnect' ? (
            <Button
              size="sm"
              icon={view.action === 'reconnect' ? <Icons.regenerate size={15} /> : undefined}
              onClick={() => setConnecting(true)}
              data-testid={view.action === 'reconnect' ? 'linked-microsoft365-reconnect' : 'linked-microsoft365-connect'}
            >
              {view.action === 'reconnect' ? t('microsoft365.user.card.reconnect') : t('microsoft365.user.card.connect')}
            </Button>
          ) : null}
        </div>
        {view.kind === 'off' ? (
          <p className="m365-card__line">
            <Icons.lock size={16} />
            <span>{t(`microsoft365.user.card.off.${offReason(status)}`, { team: status.teamName ?? '' })}</span>
          </p>
        ) : view.kind === 'consent' ? (
          <div className="m365-card__line m365-card__line--warning">
            <Icons.shield size={16} />
            <div className="m365-card__line-body">
              <span>{t('microsoft365.user.card.body.consent')}</span>
              <ConsentRequestButton />
            </div>
          </div>
        ) : view.kind === 'reconnect' ? (
          <p className="m365-card__line m365-card__line--warning">
            <Icons.regenerate size={16} />
            <span>{t('microsoft365.user.card.body.reconnect')}</span>
          </p>
        ) : null}
        {view.kind === 'connected' ? (
          <ul className="m365-linked__rows" data-testid="linked-microsoft365-features">
            {linkedRows(status).map((row) => {
              // the firm's "Neler açık" and the person's own switch; part 3's rows only while the firm has them on
              const on = row.value !== 'off' && row.value !== 'grant';
              return (
                <li key={row.feature} className="m365-linked__row" data-feature={row.feature} data-on={on ? 'true' : 'false'} data-value={row.value}>
                  <MicrosoftIcon product={row.key === 'onedrive' || row.key === 'sharepoint' ? row.key : 'outlook'} size={16} />
                  <span className="m365-linked__product">{t(`microsoft365.user.linked.rows.${row.key}`)}</span>
                  <span className={on ? 'm365-linked__value m365-linked__value--on' : row.value === 'grant' ? 'm365-linked__value m365-linked__value--grant' : 'm365-linked__value'}>
                    {t(`microsoft365.user.linked.values.${row.value}`)}
                  </span>
                  {row.value === 'grant' ? (
                    <button
                      type="button"
                      className="link-button m365-linked__grant"
                      aria-label={t('microsoft365.user.linked.grantLabel', { row: t(`microsoft365.user.linked.rows.${row.key}`) })}
                      onClick={() => setUpgrade([row.feature])}
                      data-testid="linked-microsoft365-grant"
                    >
                      {t('microsoft365.user.linked.grantAction')}
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        ) : null}
        {account ? (
          <div className="m365-linked__foot">
            <span>{t(status.features.onedrive_import || status.features.sharepoint_import ? 'microsoft365.user.linked.removeNoteDocs' : 'microsoft365.user.linked.removeNote')}</span>
            <Button size="sm" variant="danger" icon={<Icons.trash size={15} />} onClick={() => setRemoving(true)} data-testid="linked-microsoft365-remove">
              {t('microsoft365.user.card.remove')}
            </Button>
          </div>
        ) : null}
      </section>
      <p className="m365-linked__none">
        <Icons.plug size={16} />
        <span>
          <Trans i18nKey={account ? 'microsoft365.user.linked.none' : 'microsoft365.user.linked.others'} components={{ go: <Link to="/connectors" data-testid="linked-connectors" /> }} />
        </span>
      </p>
      {connecting ? <Microsoft365ConnectDialog returnTo="/settings/account" onClose={() => setConnecting(false)} onConnected={() => setConnected(true)} /> : null}
      {upgrade ? <Microsoft365ConnectDialog returnTo="/settings/account" features={upgrade} onClose={() => setUpgrade(null)} onConnected={() => setConnected(true)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={t('microsoft365.user.card.removeTitle')}
          confirmLabel={t('microsoft365.user.card.remove')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={disconnect.isPending}
          error={<ErrorAlert error={disconnect.error} />}
          onConfirm={() =>
            disconnect.mutate(undefined, {
              onSuccess: () => {
                setRemoving(false);
                setConnected(false);
              },
            })
          }
          onClose={() => {
            setRemoving(false);
            disconnect.reset();
          }}
          data-testid="microsoft365-remove-dialog"
        >
          {t('microsoft365.user.card.removeBody')}
        </ConfirmDialog>
      ) : null}
    </SettingGroup>
  );
}
